import { useCallback, useEffect, useMemo, useState } from 'react';
import { Accordion } from '@carbon/react';
import classNames from 'classnames';

import type { BBox } from 'components/query-map';

import type { SourceRow, SourceSelectionChange } from '../../types';
import { sortParamsFromState } from '../../utils';
import { FacetSection } from './facet-section';
import {
  useListedTractShapes,
  useMapFilter,
  useMapQuerySearch,
  useResultsFit,
  useResultsPaging,
} from './hooks';
import { MapFilterSection } from './map-filter-section';
import { MapQueryMap, type MapQueryFocus } from './map-query-map';
import { ResultsTable } from './results-table';
import type {
  MapQueryFilters,
  MapQueryFiltersChange,
  MapQueryResultRow,
  MapQuerySearchParams,
  MapQueryTicks,
} from './types';
import { resultsKey, rowsKey } from './utils';
import styles from './map-query-source.module.scss';

const NO_ROWS: MapQueryResultRow[] = [];

export interface MapQuerySourceProps {
  /** Ticked entities match what they hold on this date too. */
  asOfDate: string | null;
  /** The ticked entities, in tick order: the form's Map Query sources. */
  entities: SourceRow[];
  onEntitiesChange: (change: SourceSelectionChange) => void;
  /** The location ticks and the map filter, the form's too. */
  filters: MapQueryFilters;
  onFiltersChange: MapQueryFiltersChange;
  /**
   * The Tracts "Add tracts by list" matched, once each and sorted: results
   * whatever the filters, as Next adds them.
   */
  listedTractIds: string[];
  /**
   * How many assets the current filters return, once their search has answered;
   * null before that, after a failed search, and once the page unmounts.
   */
  onResultsTotalChange: (total: number | null) => void;
}

/**
 * The Map Query source: the filter panel (Map Filter, Entity, Location) beside
 * the map over the results table, which lists the matching Tracts, the listed
 * Tracts and the matching wellbore and LTD assets. The filters are the form's: the entity
 * ticks (so the header tags and title count follow them), the location ticks and
 * the map filter, so Back from Step 1 finds them as they were. Owns paging,
 * sorting and focus, and runs the search (debounced, the previous one aborted),
 * reporting its total for Next. A facet never narrows its own options, and a
 * ticked option stays listed at 0.
 *
 * @param {MapQuerySourceProps} props - The As of Date, the filters and the handlers
 * @return {JSX.Element} The Map Query page
 */
export function MapQuerySource({
  asOfDate,
  entities,
  onEntitiesChange,
  filters,
  onFiltersChange,
  listedTractIds,
  onResultsTotalChange,
}: MapQuerySourceProps) {
  const mapFilter = useMapFilter(filters, onFiltersChange);
  const { mode, polygon, spatial, aoiEditing } = mapFilter;
  const { locations } = filters;
  const [entityText, setEntityText] = useState('');
  const [locationText, setLocationText] = useState('');
  const [focus, setFocus] = useState<MapQueryFocus | null>(null);

  const entityIds = useMemo(() => entities.map(({ id }) => id), [entities]);
  const locationGroups = useMemo(
    () => locations.map(({ id }) => id),
    [locations]
  );
  const filtersKey = resultsKey({
    asOfDate: asOfDate ?? undefined,
    entityIds,
    locationGroups,
    spatial,
    listedTractIds,
  });
  const { pagination, setPagination, sorting, setSorting } =
    useResultsPaging(filtersKey);

  const params = useMemo<MapQuerySearchParams>(
    () => ({
      ...(asOfDate ? { asOfDate } : {}),
      entityIds,
      locationGroups,
      spatial,
      ...(entityText.trim() ? { entityText: entityText.trim() } : {}),
      ...(listedTractIds.length > 0 ? { listedTractIds } : {}),
      ...pagination,
      ...sortParamsFromState(sorting),
    }),
    [
      asOfDate,
      entityIds,
      locationGroups,
      spatial,
      entityText,
      listedTractIds,
      pagination,
      sorting,
    ]
  );
  const { data, answered, error, searchedExtent } = useMapQuerySearch(params);
  const listed = useListedTractShapes(listedTractIds);
  // The rows shown are behind until a search of these filters, page and sort
  // has answered; a new entity text changes none of them.
  const rowsBehind =
    error === null && (!answered || rowsKey(answered) !== rowsKey(params));
  // The map and the facet counts are behind only until these filters answer.
  const filtersBehind =
    error === null && (!answered || resultsKey(answered) !== filtersKey);

  // The answer on hand is for these filters, not one still showing for the
  // filters before: only then do its total and its shapeless Tracts count.
  const answersFilters =
    data !== undefined &&
    answered !== undefined &&
    !error &&
    resultsKey(answered) === filtersKey;
  const resultsTotal = answersFilters ? data.totalCount : null;
  useEffect(() => {
    onResultsTotalChange(resultsTotal);
    return () => onResultsTotalChange(null);
  }, [resultsTotal, onResultsTotalChange]);

  const fit = useResultsFit(resultsTotal, data, filtersKey, aoiEditing);

  const ticks = useMemo<MapQueryTicks>(
    () => ({
      entityIds,
      locationGroups,
      polygonDrawn: mode === 'aoi' && polygon !== null,
    }),
    [entityIds, locationGroups, mode, polygon]
  );

  // A row or a map click focuses an asset; a second click on it clears the focus.
  const toggleFocus = useCallback((id: string, bbox: BBox) => {
    setFocus((current) => (current?.id === id ? null : { id, bbox }));
  }, []);

  return (
    <div className={styles.source}>
      <aside
        className={classNames(styles.filters, {
          [styles.filtersSearching]: filtersBehind,
        })}
        aria-busy={filtersBehind}
      >
        <Accordion align="start">
          <MapFilterSection
            mode={mode}
            onModeChange={mapFilter.changeMode}
            hasPolygon={polygon !== null}
            aoiEditing={mapFilter.aoiEditing}
            onEdit={() => mapFilter.setAoiEditing(true)}
            onDone={() => mapFilter.setAoiEditing(false)}
            onClear={mapFilter.clearPolygon}
            noGeometryCount={answersFilters ? data.meta.noGeometryCount : null}
          />
          <FacetSection
            title="Entity"
            idPrefix="package-map-query-entity"
            searchLabel="Search entities"
            searchText={entityText}
            onSearchTextChange={setEntityText}
            buckets={data?.facets.entities.buckets}
            ticked={entities}
            onToggle={({ id, name }, checked) =>
              onEntitiesChange(
                checked
                  ? {
                      added: [{ id, name, href: `/entities/${id}` }],
                      removed: [],
                    }
                  : { added: [], removed: [id] }
              )
            }
            onClear={() => onEntitiesChange({ added: [], removed: entityIds })}
            clearLabel="Clear entities"
            emptyText="No entities own tracts in the current area."
            noMatchText="No entities match your search."
            swatches
          />
          <FacetSection
            title="Location"
            idPrefix="package-map-query-location"
            searchLabel="Search locations"
            searchText={locationText}
            onSearchTextChange={setLocationText}
            buckets={data?.facets.locations.buckets}
            ticked={locations}
            onToggle={({ id, name }, checked) =>
              onFiltersChange((current) => ({
                ...current,
                locations: checked
                  ? [...current.locations, { id, name }]
                  : current.locations.filter((location) => location.id !== id),
              }))
            }
            onClear={() =>
              onFiltersChange((current) => ({ ...current, locations: [] }))
            }
            clearLabel="Clear locations"
            emptyText="No locations hold tracts in the current area."
            noMatchText="No locations match your search."
          />
        </Accordion>
      </aside>
      <div className={styles.main}>
        <MapQueryMap
          asOfDate={asOfDate}
          mode={mode}
          polygon={polygon}
          onPolygonChange={mapFilter.setPolygon}
          aoiEditing={mapFilter.aoiEditing}
          onAoiEditingChange={mapFilter.setAoiEditing}
          extent={mapFilter.extent}
          searchedExtent={searchedExtent}
          onExtentChange={mapFilter.changeExtent}
          ticks={ticks}
          listedShapes={listed.shapes}
          nonTractFeatures={data?.nonTractFeatures}
          focus={focus}
          searching={filtersBehind || listed.loading}
          fit={fit}
          onTractClick={toggleFocus}
        />
        <ResultsTable
          rows={data?.results ?? NO_ROWS}
          totalCount={data?.totalCount ?? null}
          isLoading={rowsBehind}
          error={error ? error.message : null}
          pagination={pagination}
          onPaginationChange={setPagination}
          sorting={sorting}
          onSortingChange={setSorting}
          focusedRowId={focus?.id ?? null}
          onRowClick={toggleFocus}
        />
      </div>
    </div>
  );
}
